import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginModal } from '../components/LoginModal';
// @ts-ignore - Cesium module resolution issue
import * as Cesium from 'cesium';
const UserMismatchModal = ({ isOpen, onSignOut, onCancel }) => {
    if (!isOpen)
        return null;
    return (_jsx("div", { style: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }, children: _jsxs("div", { style: {
                backgroundColor: 'var(--sand)',
                padding: '2rem',
                borderRadius: '8px',
                maxWidth: '500px',
                margin: '1rem'
            }, children: [_jsx("h2", { style: { color: 'var(--charcoal)', marginBottom: '1rem' }, children: "Different User Account" }), _jsx("p", { style: { color: 'var(--charcoal)', marginBottom: '1.5rem' }, children: "This bundle invite was sent to a different user account. To claim this bundle, you'll need to sign out and sign in with the correct account." }), _jsxs("div", { style: { display: 'flex', gap: '1rem', justifyContent: 'flex-end' }, children: [_jsx("button", { onClick: onCancel, style: {
                                padding: '0.5rem 1rem',
                                backgroundColor: 'transparent',
                                color: 'var(--charcoal)',
                                border: '1px solid var(--charcoal)',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }, children: "Cancel" }), _jsx("button", { onClick: onSignOut, style: {
                                padding: '0.5rem 1rem',
                                backgroundColor: 'var(--charcoal)',
                                color: 'var(--sand)',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }, children: "Sign Out & Continue" })] })] }) }));
};
export const ClaimBundlePage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { isAuthenticated, isLoading: authLoading, logout } = useAuth();
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showBundleModal, setShowBundleModal] = useState(false);
    const [showMismatchModal, setShowMismatchModal] = useState(false);
    const [viewer, setViewer] = useState(null);
    const cesiumContainerRef = useRef(null);
    const assignmentId = searchParams.get('assignment_id');
    const userId = searchParams.get('user_id');
    const csrfToken = searchParams.get('csrf');
    useEffect(() => {
        // Wait for auth to load
        if (authLoading) {
            return;
        }
        // Check if we have required params
        if (!assignmentId || !userId) {
            console.error('Missing assignment_id or user_id parameters');
            navigate('/');
            return;
        }
        // If not authenticated, show login modal
        if (!isAuthenticated) {
            setShowLoginModal(true);
            return;
        }
        // User is authenticated - show the bundle purchase modal
        setShowLoginModal(false);
        setShowBundleModal(true);
    }, [isAuthenticated, authLoading, assignmentId, userId, csrfToken, navigate]);
    const handleCloseBundleModal = () => {
        setShowBundleModal(false);
        navigate('/');
    };
    const handleCloseLoginModal = () => {
        // User manually closed login modal (cancel button)
        setShowLoginModal(false);
        navigate('/');
    };
    const handleLoginSuccess = () => {
        // User successfully logged in
        // Check if there's a pending bundle claim to restore
        const pendingClaim = localStorage.getItem('pending_bundle_claim');
        if (pendingClaim) {
            try {
                const claimData = JSON.parse(pendingClaim);
                // Clear from localStorage
                localStorage.removeItem('pending_bundle_claim');
                // Redirect to the bundle claim page with the stored parameters
                navigate(`/claim-bundle?assignment_id=${claimData.assignment_id}&user_id=${claimData.user_id}&csrf=${claimData.csrf}`);
                return;
            }
            catch (e) {
                console.error('Failed to parse pending bundle claim:', e);
                localStorage.removeItem('pending_bundle_claim');
            }
        }
        // Don't close modal or navigate - let useEffect detect auth change and show bundle modal
    };
    const handleUserMismatch = () => {
        setShowMismatchModal(true);
    };
    const handleSignOutAndContinue = async () => {
        // Store the bundle claim parameters in localStorage before logout
        // This allows us to redirect back after login (including Google OAuth redirect)
        if (assignmentId && userId && csrfToken) {
            localStorage.setItem('pending_bundle_claim', JSON.stringify({
                assignment_id: assignmentId,
                user_id: userId,
                csrf: csrfToken
            }));
        }
        // Sign out
        await logout();
        // After logout, the useEffect will trigger and show login modal
        setShowMismatchModal(false);
    };
    const handleCancelMismatch = () => {
        setShowMismatchModal(false);
        navigate('/');
    };
    // Initialize Cesium viewer with rotating globe
    useEffect(() => {
        if (!cesiumContainerRef.current || viewer)
            return;
        // @ts-ignore
        Cesium.Ion.defaultAccessToken = REACT_APP_CESIUM_ION_ACCESS_TOKEN || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI0YTQzZmE4Yy04ZTVlLTQxZTItYTk0ZC00NTllZTA5ZjE2MDAiLCJpZCI6MjQ5NDYzLCJpYXQiOjE3Mjg0NDMxMzV9.bwVLFUIbbhVpeMv1bBqxXEbqQo5xJ2-LqBUgOkKqHkA';
        try {
            // @ts-ignore - Cesium typing issues
            const newViewer = new Cesium.Viewer(cesiumContainerRef.current, {
                baseLayerPicker: false,
                geocoder: false,
                homeButton: false,
                sceneModePicker: false,
                navigationHelpButton: false,
                animation: false,
                timeline: false,
                fullscreenButton: false,
                vrButton: false,
                selectionIndicator: false,
                infoBox: false,
                creditContainer: document.createElement('div'),
                // @ts-ignore
                imageryProvider: new Cesium.IonImageryProvider({ assetId: 3954 })
            });
            setViewer(newViewer);
            // Zoom out to show full globe
            newViewer.camera.setView({
                destination: Cesium.Cartesian3.fromDegrees(0, 20, 20000000), // 20M meters altitude for full globe view
                orientation: {
                    heading: Cesium.Math.toRadians(0.0),
                    pitch: Cesium.Math.toRadians(-30.0), // Look down at angle
                    roll: 0.0
                }
            });
            // Disable all user interactions
            const controller = newViewer.scene.screenSpaceCameraController;
            controller.enableRotate = false;
            controller.enableTranslate = false;
            controller.enableZoom = false;
            controller.enableTilt = false;
            controller.enableLook = false;
            // Start rotating the globe
            let lastTime = Date.now();
            const spinRate = 0.02; // Radians per second
            const preRenderListener = () => {
                const currentTime = Date.now();
                const deltaTime = (currentTime - lastTime) / 1000;
                lastTime = currentTime;
                const angle = -spinRate * deltaTime;
                newViewer.scene.camera.rotate(Cesium.Cartesian3.UNIT_Z, angle);
            };
            newViewer.scene.preRender.addEventListener(preRenderListener);
            // Cleanup
            return () => {
                if (newViewer && !newViewer.isDestroyed()) {
                    newViewer.scene.preRender.removeEventListener(preRenderListener);
                    newViewer.destroy();
                }
            };
        }
        catch (error) {
            console.error('Failed to initialize Cesium viewer:', error);
        }
    }, [viewer]);
    if (authLoading) {
        return (_jsx("div", { className: "flex items-center justify-center min-h-screen", style: { backgroundColor: 'var(--sand)' }, children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4", style: { borderColor: 'var(--forest)' } }), _jsx("p", { style: { color: 'var(--charcoal)' }, children: "Loading..." })] }) }));
    }
    if (!assignmentId || !userId) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx("div", { ref: cesiumContainerRef, style: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 0
                } }), _jsx(LoginModal, { isOpen: showLoginModal, onClose: handleCloseLoginModal, onLoginSuccess: handleLoginSuccess, message: "You've been invited to claim a LandScope bundle. Please log in to continue." }), _jsx(UserMismatchModal, { isOpen: showMismatchModal, onSignOut: handleSignOutAndContinue, onCancel: handleCancelMismatch }), showBundleModal && (_jsx("div", { className: "fixed inset-0 z-[70] flex items-center justify-center", style: { backgroundColor: 'rgba(0,0,0,0.6)' }, children: _jsxs("div", { className: "rounded-lg p-6 max-w-md w-full mx-4", style: { backgroundColor: 'var(--charcoal)', border: '1px solid var(--pewter)' }, children: [_jsx("h3", { className: "text-lg font-semibold mb-4", style: { color: 'var(--sand)' }, children: "Bundle Claims Retired" }), _jsx("p", { className: "text-sm mb-6", style: { color: 'var(--dew)' }, children: "Project bundles have been replaced by our new tier-based plans. Please visit your billing dashboard to explore available plans." }), _jsx("button", { onClick: handleCloseBundleModal, className: "px-4 py-2 rounded text-sm font-medium w-full", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: "Close" })] }) }))] }));
};
